/**
 * 결함 형상을 IR 등 픽셀 좌표 오버레이(팝업 SVG 등)로 그리기 위한 어댑터.
 * 표시: Polygon 4점이면 OBB. 모달 편집 박스는 그 envelope AABB.
 */

/** @typedef {'polygon_aabb' | 'oriented_rect'} DefectShapeOverlayKind */

export const DefectShapeOverlayKind = Object.freeze({
  POLYGON_AABB: 'polygon_aabb',
  ORIENTED_RECT: 'oriented_rect'
});

/**
 * @param {{ type?: string, coordinates?: unknown, shapeKind?: string }} [geom]
 * @returns {DefectShapeOverlayKind}
 */
export function inferDefectShapeOverlayKind(geom) {
  if (!geom || typeof geom !== 'object') return DefectShapeOverlayKind.POLYGON_AABB;
  const ext = geom.shapeKind || geom.defectShapeKind;
  if (ext === 'oriented_rect' || ext === 'OBB' || ext === DefectShapeOverlayKind.ORIENTED_RECT) {
    return DefectShapeOverlayKind.ORIENTED_RECT;
  }
  return DefectShapeOverlayKind.POLYGON_AABB;
}

/**
 * @param {unknown} pt
 * @returns {number[] | null} [x, y]
 */
function pointPair(pt) {
  if (Array.isArray(pt) && pt.length >= 2 && !Array.isArray(pt[0])) {
    const x = Number(pt[0]);
    const y = Number(pt[1]);
    if (Number.isFinite(x) && Number.isFinite(y)) {
      return [x, y];
    }
  }
  if (pt && typeof pt === 'object') {
    const x = Number(pt.x ?? pt.X ?? pt.lon ?? pt.lng);
    const y = Number(pt.y ?? pt.Y ?? pt.lat);
    if (Number.isFinite(x) && Number.isFinite(y)) {
      return [x, y];
    }
  }
  return null;
}

/**
 * @param {unknown} pt
 */
function isPositionPair(pt) {
  return pointPair(pt) != null;
}

/**
 * GeoJSON Polygon/MultiPolygon·레거시(링이 coordinates 바로 아래) → 첫 링 좌표 배열.
 * @param {unknown} coords
 * @returns {number[][] | null}
 */
function ringFromCoordinates(coords) {
  if (!Array.isArray(coords) || coords.length === 0) {
    return null;
  }
  if (isPositionPair(coords[0])) {
    return coords;
  }
  if (Array.isArray(coords[0]) && isPositionPair(coords[0][0])) {
    return coords[0];
  }
  if (
    Array.isArray(coords[0]) &&
    Array.isArray(coords[0][0]) &&
    isPositionPair(coords[0][0][0])
  ) {
    return coords[0][0];
  }
  return null;
}

/**
 * REST/Geolatte 응답을 팝업·DrawableImage가 기대하는 GeoJSON Polygon으로 통일.
 * @param {unknown} raw
 * @returns {{ type: string, coordinates: number[][][] } | null}
 */
export function normalizeDefectShapeGeom(raw) {
  if (raw == null) {
    return null;
  }
  let g = raw;
  if (typeof g === 'string') {
    try {
      g = JSON.parse(g);
    } catch {
      return null;
    }
  }
  if (typeof g !== 'object') {
    return null;
  }
  if (g.geometry && typeof g.geometry === 'object') {
    return normalizeDefectShapeGeom(g.geometry);
  }
  if (!g.coordinates) {
    return null;
  }
  const ring = ringFromCoordinates(g.coordinates);
  if (!ring || ring.length < 3) {
    return null;
  }
  // 수동 결함: 서버가 픽셀 사각을 LineString 4점으로 내려줌(AI는 Polygon). 오버레이는 Polygon만 처리.
  return { type: 'Polygon', coordinates: [ring] };
}

/**
 * Polygon 첫 링에서 사각형 오버레이용 꼭짓점 배열 추출 (닫힌 링 마지막 점 제거).
 * @returns {number[][]} [[x,y], ...] 최소 4점
 */
export function polygonFirstRingToCorners(geom) {
  if (!geom || geom.type !== 'Polygon' || !geom.coordinates?.[0]) return [];
  let ring = geom.coordinates[0];
  if (ring.length >= 2) {
    const a = ring[0];
    const b = ring[ring.length - 1];
    if (a && b && a[0] === b[0] && a[1] === b[1]) {
      ring = ring.slice(0, -1);
    }
  }
  if (ring.length === 4) {
    return ring;
  }
  if (ring.length > 4) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    ring.forEach((pt) => {
      if (!pt || pt.length < 2) return;
      const x = pt[0];
      const y = pt[1];
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    });
    if (minX !== Infinity && maxX !== minX && maxY !== minY) {
      return [
        [minX, minY],
        [maxX, minY],
        [maxX, maxY],
        [minX, maxY]
      ];
    }
  }
  return [];
}

/**
 * @param {{ type?: string, coordinates?: unknown, center?: number[], width?: number, height?: number, rotation?: number }} geom
 * @returns {number[][]}
 */
function orientedPlaceholderCorners(geom) {
  const cx = geom.center?.[0];
  const cy = geom.center?.[1];
  const w = geom.width;
  const h = geom.height;
  if (
    typeof cx !== 'number' ||
    typeof cy !== 'number' ||
    typeof w !== 'number' ||
    typeof h !== 'number' ||
    w <= 0 ||
    h <= 0
  ) {
    return [];
  }
  const hw = w / 2;
  const hh = h / 2;
  return [
    [cx - hw, cy - hh],
    [cx + hw, cy - hh],
    [cx + hw, cy + hh],
    [cx - hw, cy + hh]
  ];
}

/**
 * 결함 지오메트리 → 픽셀 공간에서의 한 개 사각형 링(4점).
 * @param {object} geom GeoJSON-like 또는 향후 OBB 필드
 * @param {DefectShapeOverlayKind} [kind]
 * @returns {number[][] | null}
 */
export function defectGeomToOverlayQuadPx(geom, kind = inferDefectShapeOverlayKind(geom)) {
  if (kind === DefectShapeOverlayKind.ORIENTED_RECT) {
    const c = orientedPlaceholderCorners(geom);
    return c.length >= 4 ? c : null;
  }
  const corners = polygonFirstRingToCorners(geom);
  return corners.length >= 4 ? corners : null;
}

/**
 * 네이티브 픽셀 좌표 → 표시 크기 기준 스케일
 * @param {number[][]} quad
 * @param {number} nativeW
 * @param {number} nativeH
 * @param {number} displayW
 * @param {number} displayH
 * @returns {number[][]}
 */
export function scaleQuadToDisplay(quad, nativeW, nativeH, displayW, displayH) {
  const nw = Math.max(1, nativeW);
  const nh = Math.max(1, nativeH);
  const sx = displayW / nw;
  const sy = displayH / nh;
  return quad.map(([x, y]) => [x * sx, y * sy]);
}

/**
 * PanelDefect.defectShape(left_x, top_y, width, height) → GeoJSON Polygon
 * @param {object} shape
 */
export function panelDefectShapeToGeom(shape) {
  if (!shape || typeof shape !== 'object') {
    return null;
  }
  const x = Number(shape.left_x ?? shape.leftX);
  const y = Number(shape.top_y ?? shape.topY);
  const w = Number(shape.width);
  const h = Number(shape.height);
  if (
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    !Number.isFinite(w) ||
    !Number.isFinite(h) ||
    w <= 0 ||
    h <= 0
  ) {
    return null;
  }
  return {
    type: 'Polygon',
    coordinates: [
      [
        [x, y],
        [x + w, y],
        [x + w, y + h],
        [x, y + h],
        [x, y]
      ]
    ]
  };
}

/**
 * WFS module_defects / solar_module 피처에서 패널 PK(solar_module.seq) 추출.
 * GeoServer 레이어·뷰마다 속성명이 다를 수 있어 후보 키를 순회한다.
 * module_id(업무용 문자열 ID)는 solar_module_seq(PK)와 다르므로 후보에 넣지 않는다.
 * @param {object} props
 * @returns {number | null}
 */
export function resolveWfsSolarModuleSeq(props) {
  if (!props || typeof props !== 'object') {
    return null;
  }
  const candidates = [
    props.solar_module_seq,
    props.solarModuleSeq,
    props.module_seq,
    props.solar_module_id
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const n = Number(candidates[i]);
    if (Number.isFinite(n) && n > 0) {
      return n;
    }
  }
  return null;
}

/**
 * WFS·REST·store 공통 moduleDefect PK (seq/id/defect_seq 등).
 * @param {object} props
 * @returns {number | null}
 */
export function resolveModuleDefectPk(props) {
  if (!props || typeof props !== 'object') {
    return null;
  }
  const candidates = [
    props.seq,
    props.id,
    props.defect_seq,
    props.module_defect_seq,
    props.moduleDefectSeq
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const n = Number(candidates[i]);
    if (Number.isFinite(n) && n > 0) {
      return n;
    }
  }
  return null;
}

/**
 * @param {object} defect
 * @param {number|string} pk
 */
export function moduleDefectMatchesPk(defect, pk) {
  const pid = Number(pk);
  if (!Number.isFinite(pid) || pid <= 0) {
    return false;
  }
  return (
    Number(defect?.id) === pid ||
    Number(defect?.seq) === pid ||
    Number(defect?.moduleDefectSeq) === pid ||
    Number(defect?.defect_seq) === pid ||
    Number(defect?.module_defect_seq) === pid
  );
}

/**
 * API 본문이 defectShapeGeom:null 로 shape를 덮어쓰지 않도록, shape 있는 소스만 필드 병합.
 * @param {...object|null|undefined} sources 앞쪽이 WFS 등 베이스, 뒤쪽이 store/list/API 보강
 */
export function mergeDefectRecordForShapeOverlay(...sources) {
  const merged = Object.assign(
    {},
    ...sources.filter((s) => s && typeof s === 'object')
  );
  for (let i = sources.length - 1; i >= 0; i -= 1) {
    const src = sources[i];
    if (!src || typeof src !== 'object') {
      continue;
    }
    if (!resolveDefectShapeGeomForOverlay(src)) {
      continue;
    }
    if (src.defectShapeGeom != null) {
      merged.defectShapeGeom = src.defectShapeGeom;
    }
    if (src.defectShape != null) {
      merged.defectShape = src.defectShape;
    }
    if (src.panelDefect != null) {
      merged.panelDefect = src.panelDefect;
    }
    return merged;
  }
  return merged;
}

/**
 * IR 오버레이·DrawableImage 공통: defectShapeGeom 우선, 없으면 defectShape 박스.
 * @param {object} defect
 */
export function resolveDefectShapeGeomForOverlay(defect) {
  if (!defect || typeof defect !== 'object') {
    return null;
  }
  const fromGeom = normalizeDefectShapeGeom(defect.defectShapeGeom);
  if (fromGeom) {
    return fromGeom;
  }
  const shape = defect.defectShape || defect.panelDefect;
  return panelDefectShapeToGeom(shape);
}

/**
 * 축정렬 사각이면 모달 AABB 편집 박스가 곧 표시라 OBB 윤곽을 겹쳐 그리지 않는다.
 * @param {number[][]} quad
 */
export function isAxisAlignedQuad(quad) {
  if (!Array.isArray(quad) || quad.length < 4) {
    return false;
  }
  const xs = new Set();
  const ys = new Set();
  for (let i = 0; i < 4; i += 1) {
    const x = Number(quad[i]?.[0]);
    const y = Number(quad[i]?.[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return false;
    }
    xs.add(x);
    ys.add(y);
  }
  return xs.size === 2 && ys.size === 2;
}

/**
 * 결함 수정 모달: 기울어진 OBB만 표시 윤곽으로 반환. 축정렬이면 null.
 * @param {object} defect
 * @returns {number[][] | null}
 */
export function resolveModalObbDisplayQuad(defect) {
  const geom = resolveDefectShapeGeomForOverlay(defect);
  if (!geom) {
    return null;
  }
  const quad = defectGeomToOverlayQuadPx(geom);
  if (!quad || isAxisAlignedQuad(quad)) {
    return null;
  }
  return quad;
}

/**
 * 결함 수정 모달 편집 박스: OBB(또는 AABB) 4점의 축정렬 envelope.
 * @param {object} defect
 * @returns {{ x: number, y: number, w: number, h: number } | null}
 */
export function editAabbFromDefect(defect) {
  const geom = resolveDefectShapeGeomForOverlay(defect);
  if (!geom) {
    return null;
  }
  const quad = defectGeomToOverlayQuadPx(geom);
  if (!quad) {
    return null;
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  quad.forEach(pt => {
    const x = Number(pt?.[0]);
    const y = Number(pt?.[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return;
    }
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  });
  const w = maxX - minX;
  const h = maxY - minY;
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return null;
  }
  return { x: minX, y: minY, w, h };
}

/**
 * 픽셀 geom → 팝업 SVG drawMarkers용 4꼭짓점(표시 크기 기준).
 * @param {object} defect
 * @param {number} nativeW
 * @param {number} nativeH
 * @param {number} displayW
 * @param {number} displayH
 * @returns {number[][] | null}
 */
export function defectOverlayQuadForDisplay(
  defect,
  nativeW,
  nativeH,
  displayW,
  displayH
) {
  const geom = resolveDefectShapeGeomForOverlay(defect);
  if (!geom) {
    return null;
  }
  const quadPx = defectGeomToOverlayQuadPx(geom);
  if (!quadPx) {
    return null;
  }
  return scaleQuadToDisplay(
    quadPx,
    nativeW,
    nativeH,
    displayW,
    displayH
  );
}
